import { useLingui } from '@lingui/react/macro'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import type { ChatPart } from 'features/timeSeries/agentTab/chatStream'
import { toolSentence } from 'features/timeSeries/agentTab/toolSentence'
import React from 'react'
import styled from 'styled-components'

import { chipLabel, shortTag } from './tagLabel'
import { useTagMention } from './useTagMention'

/**
 * One tool call, as a quiet line rather than a panel.
 *
 * What a reader wants is the sentence — "searched the drawings for jacket
 * water" — and the candidates it turned up. A step that found nothing just
 * has no chips; only a tool failure gets a line of its own. The payload is
 * never shown, since a block of JSON in a conversation is noise however folded.
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Summary = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  padding: 0.1875rem 0.25rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Dot = styled.span`
  flex-shrink: 0;
  width: 0.375rem;
  height: 0.375rem;
  margin: 0.375rem 0.1875rem 0;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.5;
`

const ToolSpinner = styled(Spinner)`
  flex-shrink: 0;
  margin-top: 0.125rem;
`

const Outcome = styled(Text)`
  padding-left: 1.375rem;
`

/** The tool's own failure text; a step that simply found nothing says nothing. */
const toolError = (result: unknown): string | null => {
  if (!result || typeof result !== 'object') return null
  const error = (result as Record<string, unknown>).error
  return typeof error === 'string' && error ? error : null
}

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding-left: 1.25rem;
`

const Chip = styled(Text)<{ $dim?: boolean; $onChart?: boolean }>`
  border: 1px solid
    ${({ $onChart, theme }) =>
      $onChart ? theme.forecast.primary : theme.forecast.cardDividerBackground};
  border-radius: 999px;
  padding: 0.0625rem 0.5rem;
  white-space: nowrap;
  cursor: pointer;
  opacity: ${({ $dim }) => ($dim ? 0.5 : 1)};
  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

/**
 * Candidate tags surface the moment a search or coverage probe returns — the
 * "watch it pick tags" feel, without expanding raw JSON.
 */
const ToolChips: React.FC<{ tool: string; result: unknown }> = ({
  tool,
  result,
}) => {
  const { t } = useLingui()
  const body = result as Record<string, unknown> | null
  const mention = useTagMention()
  if (!body || typeof body !== 'object') return null
  const handlers = (id: string) => ({
    $onChart: mention.isOnChart(id),
    title: mention.isOnChart(id)
      ? t`On the chart`
      : t`Click to add to the chart`,
    onMouseEnter: () => mention.hover(id),
    onMouseLeave: mention.release,
    onClick: () => mention.add(id),
  })

  if (tool === 'search_tags' && Array.isArray(body.tags)) {
    const tags = body.tags as {
      id: string
      unit?: string
      name?: string
      description?: string
    }[]
    if (!tags.length) return null
    return (
      <ChipRow>
        {tags.slice(0, 10).map((tag) => (
          <Chip key={tag.id} variant='caption' {...handlers(tag.id)}>
            {chipLabel(tag)}
          </Chip>
        ))}
        {tags.length > 10 && (
          <Chip variant='caption' $dim>
            +{tags.length - 10} more
          </Chip>
        )}
      </ChipRow>
    )
  }

  if (
    tool === 'check_coverage' &&
    body.coverage &&
    typeof body.coverage === 'object'
  ) {
    const entries = Object.entries(body.coverage as Record<string, number>)
    if (!entries.length) return null
    return (
      <ChipRow>
        {entries.slice(0, 14).map(([tag, fraction]) => (
          <Chip
            key={tag}
            variant='caption'
            $dim={fraction < 0.5}
            {...handlers(tag)}>
            {shortTag(tag)} {Math.round(fraction * 100)}%
          </Chip>
        ))}
      </ChipRow>
    )
  }

  return null
}

export const ToolCard: React.FC<{
  part: Extract<ChatPart, { kind: 'tool' }>
  /** The agent's own reason for the call, when one was inferred. */
  caption?: string
}> = ({ part, caption }) => {
  const done = part.result !== undefined
  const outcome = done ? toolError(part.result) : null
  return (
    <Wrap>
      <Summary>
        {done ? <Dot aria-hidden='true' /> : <ToolSpinner size={12} />}
        <span>
          <Text variant='caption'>
            {toolSentence(part.tool, part.args, done)}
          </Text>
          {caption && (
            <Text as='div' variant='caption' color='tertiary'>
              {caption}
            </Text>
          )}
        </span>
      </Summary>
      {outcome && (
        <Outcome as='div' variant='caption' color='tertiary'>
          {outcome}
        </Outcome>
      )}
      {done && <ToolChips tool={part.tool} result={part.result} />}
    </Wrap>
  )
}
